import { useId } from 'react';
import Icon from './Icon';

/**
 * One major section of the checkout form: step badge, heading, optional status, then content.
 *
 * NOT A CARD ANY MORE, despite the name. This used to render a white rounded box floating on a
 * gray page; the redesign renders full-bleed bands divided by 1px hairlines. The name is kept
 * because every call site and the handoff's screen map use it, and a rename would churn more
 * than it clarifies.
 *
 * Renders a <section aria-labelledby> rather than a bare <div>. The original markup put an
 * <h3> inside a plain div with no programmatic link to the controls it labelled, so screen
 * reader users got no grouping. Fixing it here fixes it for every section at once.
 *
 * The heading is an <h2>, not the <h3> it was. The page's only <h1> is the hero, so <h3> skipped
 * a level -- which is exactly the kind of thing that is invisible on screen and confusing in a
 * screen reader's document outline.
 *
 * @param {object} props
 * @param {string} props.title            section heading text
 * @param {number} [props.stepNumber]     1-4; renders the dark numeral badge
 * @param {string} [props.icon]           legacy icon badge, used only when stepNumber is absent
 * @param {string} [props.description]    sub-text under the heading
 * @param {React.ReactNode} [props.tag]    slot ADJACENT to the heading, for a qualifier that
 *                                         belongs to the title rather than the section body
 * @param {React.ReactNode} [props.status] right-aligned slot: a completion check or "1 of 3 answered"
 * @param {boolean} [props.tinted]        warm ground for the no-charge section
 * @param {string} [props.className]      extra classes on the wrapper
 * @param {React.ReactNode} props.children
 */
export default function SectionCard({
  title,
  stepNumber,
  icon,
  description,
  tag,
  status,
  tinted = false,
  className = '',
  children,
}) {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className={`border-b border-gray-200 px-4 py-5 sm:px-7 sm:py-6 ${
        tinted ? 'bg-interest' : 'bg-white'
      } ${className}`}
    >
      {/*
        `flex-wrap` matters once a `tag` is present: at 375px the heading already wraps to two
        lines, and without wrapping the tag would compress the h2 rather than move below it.
      */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
        {stepNumber != null ? (
          /*
            aria-hidden because the number is repeated as an sr-only prefix inside the heading
            below. Left readable, a screen reader announces a bare "1" adrift from the heading
            it belongs to; hidden and prefixed, it hears "Step 1: Your details".
          */
          <span
            aria-hidden="true"
            className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center bg-gray-900 text-chip font-semibold tabular-nums tracking-normal text-white sm:h-5 sm:w-5"
          >
            {stepNumber}
          </span>
        ) : (
          icon && (
            // Pre-redesign badge. Only the feature-flagged add-on sections still reach this
            // branch; it goes away with them in the final cleanup phase.
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100">
              <Icon name={icon} className="h-5 w-5 text-primary-600" />
            </div>
          )
        )}

        <h2
          id={headingId}
          className="text-lg font-semibold tracking-heading text-gray-900 sm:text-xl"
        >
          {stepNumber != null && <span className="sr-only">Step {stepNumber}: </span>}
          {title}
        </h2>

        {/*
          Adjacent to the heading, NOT right-aligned. A qualifier like "no cost to ask" modifies
          the title, so it has to read as part of it -- pushed to the far right it becomes a
          second status field competing with the answered count.
        */}
        {tag}

        {/*
          `ml-auto` rather than `justify-between` on the row: the heading must stay adjacent to
          its badge when there is no status, and space-between would push them apart.
        */}
        {status && <div className="ml-auto flex flex-shrink-0 items-center">{status}</div>}
      </div>

      {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}

      {/*
        `space-y` lives here, not on the <section> where it used to be. Several call sites
        (CustomerDetailsSection's three field rows, for one) pass a flat list of siblings and
        rely on the parent to space them, so removing it entirely would collapse them together.
      */}
      <div className="mt-4 space-y-3 sm:space-y-4">{children}</div>
    </section>
  );
}
